import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  width?: string;
  render: (item: T, index: number, onChange: (value: T) => void) => React.ReactNode;
}

export interface DynamicTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onChange: (data: T[]) => void;
  createNewItem: () => T;
  minRows?: number;
  maxRows?: number;
  addButtonText?: string;
  disabled?: boolean;
  error?: string;
  title?: string;
  description?: string;
}

export function DynamicTable<T extends { id: string }>({
  columns,
  data,
  onChange,
  createNewItem,
  minRows = 0,
  maxRows = 50,
  addButtonText,
  disabled = false,
  error,
  title,
  description,
}: DynamicTableProps<T>) {
  const { t } = useTranslation();
  const resolvedAddButtonText = addButtonText || t('common.addRow');
  const handleAddRow = () => {
    if (data.length < maxRows) {
      onChange([...data, createNewItem()]);
    }
  };

  const handleRemoveRow = (index: number) => {
    if (data.length > minRows) {
      onChange(data.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, updatedItem: T) => {
    const newData = [...data];
    newData[index] = updatedItem;
    onChange(newData);
  };

  return (
    <div className="space-y-4">
      {title && (
        <div>
          <h4 className="text-sm font-medium text-gray-700">{title}</h4>
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" aria-label={title ?? t('accessibility.dataTable')}>
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  style={{ width: column.width }}
                >
                  {column.header}
                </th>
              ))}
              {!disabled && (
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  {t('common.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (disabled ? 0 : 1)}
                  className="px-3 py-8 text-center text-sm text-gray-500"
                >
                  {t('common.noRecords')}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td key={`${item.id}-${String(column.key)}`} className="px-3 py-2">
                      {column.render(item, index, (value) => handleItemChange(index, value))}
                    </td>
                  ))}
                  {!disabled && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(index)}
                        disabled={data.length <= minRows}
                        aria-label={t('accessibility.removeRow', { number: index + 1 })}
                        className={`
                          p-1 rounded hover:bg-red-100 transition-colors
                          ${data.length <= minRows ? 'opacity-50 cursor-not-allowed' : 'text-red-600'}
                        `}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!disabled && data.length < maxRows && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddRow}
          aria-label={resolvedAddButtonText}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          {resolvedAddButtonText}
        </Button>
      )}

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default DynamicTable;
